import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import DOMPurify from 'dompurify';
import EmailInboxService from '../../../Services/EmailInboxService';
import { splitQuotedHtml, splitQuotedText } from '../../../Utils/quotedEmail';

/**
 * One message's full body, fetched on demand, SANITISED, and with the thread
 * it was written under FOLDED AWAY.
 *
 * ── WHY THE ALLOWLIST ──
 * This is mail somebody else wrote. It is the most hostile input this
 * dashboard renders, and it reaches a logged-in agent's session. The tag and
 * attribute allowlist below is the one Components/NotificationBell.jsx
 * established, which is the pattern this repo already got right; two other
 * screens render email HTML without it and should be brought to this, not
 * matched.
 *
 * `blockquote` AND `hr` ARE ADDED TO IT HERE, and the reason is the fold: the
 * quoted tail is identified by its wrapper (see Utils/quotedEmail), so a
 * sanitiser that dropped `blockquote` left a customer's quote of our own
 * template arriving UNWRAPPED in the timeline and reading as something they
 * had just written. Both tags are inert containers and neither widens what
 * the markup can do.
 *
 * `img` IS STILL DELIBERATELY ABSENT, inherited from that same pattern and
 * worth keeping on purpose: a remote image in a customer's mail is also a
 * read receipt, and a console should not tell a sender which of their
 * messages the desk opened and when. Mail that is all pictures therefore
 * reads as text — the right trade for a reader.
 *
 * ── THE QUOTE IS COLLAPSED BY DEFAULT, AND SAYS HOW MUCH IT IS HIDING ──
 * These threads carry enormous tails; one message in the archive is 50KB of
 * which the new text is 3KB. So the new text is what renders and the history
 * is one line behind a toggle — with its size on the toggle, because "show
 * trimmed content" that could be two lines or forty is a thing a reader has
 * to click to find out.
 *
 * ── WHY A BODY CAN BE TEXT-ONLY ──
 * Past ~90 days the `.eml` has aged into Glacier and the endpoint answers
 * from the knowledge-base sanitiser's copy instead (`source: 'sanitized'`):
 * plain text, quoted history and attachments already cut, no HTML at all. So
 * a body with no markup is not a rendering fault, and the screen says which
 * copy it is showing rather than leaving an agent to wonder why a mail they
 * remember as branded looks bare.
 */
const ALLOWED_TAGS = ['p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'blockquote', 'hr'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style'];

const clean = (html) => DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });

/** Roughly how much is behind the fold, in the unit a reader can judge. */
const foldSize = (quoted) => {
  const lines = String(quoted).split('\n').length;
  const kb = Math.round(String(quoted).length / 1024);
  return kb >= 2 ? `${kb}KB of history` : `${lines} more lines`;
};

function MessageBody({ messageId }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQuoted, setShowQuoted] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    EmailInboxService.getEmail(messageId)
      .then((result) => {
        if (live) setContent(result);
      })
      .catch((err) => {
        if (live) setError(err?.response?.data?.message || 'Could not load this message');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [messageId]);

  /**
   * Split, then sanitise each half — in that order, and memoised because both
   * steps parse the whole body and a 50KB mail is not something to re-parse
   * on every keystroke elsewhere on the page. See Utils/quotedEmail for why
   * the order is load-bearing rather than a preference.
   */
  const parts = useMemo(() => {
    const html = content?.html;
    const text = content?.text;
    if (html) {
      const { latest, quoted } = splitQuotedHtml(html);
      return { html: clean(latest), quotedHtml: quoted ? clean(quoted) : '', quoted };
    }
    if (text) {
      const { latest, quoted } = splitQuotedText(text);
      return { text: latest, quotedText: quoted, quoted };
    }
    return null;
  }, [content]);

  if (loading) {
    return (
      <div className="cs-body cs-body--loading">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading message
      </div>
    );
  }

  if (error) {
    return <Alert variant="warning" className="py-1 px-2 small mb-0">{error}</Alert>;
  }

  if (!parts) {
    return <div className="cs-body text-muted">This message has no stored body.</div>;
  }

  return (
    <div className="cs-body">
      {content?.source === 'sanitized' && (
        <div className="cs-body__note">
          Reduced copy — the original has aged into cold storage, so quoted
          history, attachments and formatting are already stripped.
        </div>
      )}
      {parts.html ? (
        <div
          className="cs-body__html"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: parts.html }}
        />
      ) : (
        <pre className="cs-body__text">{parts.text}</pre>
      )}

      {!!parts.quoted && (
        <>
          <button
            type="button"
            className="cs-body__fold"
            aria-expanded={showQuoted}
            onClick={() => setShowQuoted((was) => !was)}
          >
            {showQuoted ? 'Hide the thread below' : `Show the thread below — ${foldSize(parts.quoted)}`}
          </button>
          {showQuoted && (parts.quotedHtml ? (
            <div
              className="cs-body__quoted"
              data-testid="cs-quoted"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: parts.quotedHtml }}
            />
          ) : (
            <pre className="cs-body__quoted cs-body__text" data-testid="cs-quoted">
              {parts.quotedText}
            </pre>
          ))}
        </>
      )}
    </div>
  );
}

export default MessageBody;
